export type ExpenseStatus =
  | 'draft'
  | 'pending_review'
  | 'suggestion_ready'
  | 'awaiting_payment_approval'
  | 'payment_queued'
  | 'payment_initiated'
  | 'paid'
  | 'closed'
  | 'rejected';

export interface AdditionalInfo {
  attendees?: number;
  business_purpose?: string;
  km?: number;
  vehicle_type?: string;
}

/**
 * A participant in a representation event (customer or staff representation).
 *
 * NOTE: For the `employee` variant, `name` and `company` are intentionally
 * snapshotted at the time of recording — not looked up live from the
 * employees table. This is a historical audit trail: the LLM bookkeeping
 * prompt and future audits need the names/companies as they were on the
 * expense date, even if the employee is later renamed, reassigned, or
 * leaves the company. `employee_id` remains as a reference for traceability.
 */
export type Participant =
  | {
      source: 'employee';
      employee_id: string;
      name: string;
      company: string;
    }
  | {
      source: 'external';
      name: string;
      company: string;
    };

/**
 * Category-specific metadata stored on financial_documents.category_metadata.
 * Only populated for categories whose `requirements` include the matching key
 * (see EXPENSE_CATEGORY_INFO).
 */
export interface CategoryMetadata {
  participants?: Participant[];
}

/** API response: expense_reimbursements joined with financial_documents */
export interface ExpenseReimbursement {
  // From expense_reimbursements
  id: string;
  company_id: string;
  employee_id: string | null;
  financial_document_id: string;
  needs_additional_info: boolean;
  additional_info: AdditionalInfo | null;
  vat_deductible_percent: number;
  sek_amount: number | null;
  status: ExpenseStatus;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  payable_id: string | null;
  created_at: string;
  updated_at: string;
  // From financial_documents (joined)
  vendor: string | null;
  amount: number | null;
  currency: string | null;
  expense_date: string | null;
  vat_amount: number | null;
  expense_category: string | null;
  remittance_information: string | null;
  payment_method: string | null;
  extraction_status: string | null;
  extraction_confidence: number | null;
  receipt_file_path: string | null;
  receipt_file_name: string | null;
  // From employees (joined)
  employee_name: string;
  employee_iban: string | null;
  // From financial_documents.category_metadata (joined)
  category_metadata: CategoryMetadata | null;
}

/** Status display configuration for expense list */
export const EXPENSE_STATUS_DISPLAY: Record<ExpenseStatus, {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  description: string;
}> = {
  draft: { label: 'Processing', variant: 'secondary', description: 'Receipt is being processed' },
  pending_review: { label: 'Under review', variant: 'secondary', description: 'Bookkeeping suggestion being generated' },
  suggestion_ready: { label: 'Under review', variant: 'secondary', description: 'Bookkeeping suggestion ready for review' },
  awaiting_payment_approval: { label: 'Ready to approve', variant: 'default', description: 'Suggestion approved, ready for payment approval' },
  payment_queued: { label: 'Awaiting payment', variant: 'default', description: 'Approved, awaiting payment' },
  payment_initiated: { label: 'Payment scheduled', variant: 'secondary', description: 'Payment signed, awaiting payment date' },
  paid: { label: 'Paid', variant: 'default', description: 'Reimbursement complete' },
  closed: { label: 'Paid & booked', variant: 'default', description: 'Reimbursement complete with closing entry' },
  rejected: { label: 'Rejected', variant: 'destructive', description: 'Expense was rejected' },
};
